import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPlus } from 'react-icons/fi';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useEstimatesInfinite } from '../hooks/useEstimatesInfinite';
import InvoiceFormWizard from './Invoices/components/InvoiceFormWizard';
import EstimateTable from './Estimates/components/EstimateTable';

const EstimatesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isWizardModalOpen, setIsWizardModalOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [pendingConvertEstimateId, setPendingConvertEstimateId] = useState<string | null>(null);
  const [statusWarningDialogOpen, setStatusWarningDialogOpen] = useState(false);
  const [pendingStatusEstimateId, setPendingStatusEstimateId] = useState<string | null>(null);
  const [pendingStatusValue, setPendingStatusValue] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    estimates,
    error: fetchError,
    isLoading,
    isLoadingMore,
    isReachingEnd,
    loadMore,
    mutate,
    isEmpty,
  } = useEstimatesInfinite(searchTerm, statusFilter);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('refresh') === 'true') {
      navigate(location.pathname, { replace: true });
      mutate();
    }
  }, [location.pathname, location.search, mutate, navigate]);

  const transformedEstimates = useMemo(() => (
    estimates.map((estimate: any) => ({
      ...estimate,
      id: String(estimate.id),
      customer_id: estimate.customer_id || '',
      total_amount: Number(estimate.total_amount || estimate.sub_total || 0),
      sub_total: Number(estimate.sub_total || 0),
      tax_total: Number(estimate.tax_total || 0),
    }))
  ), [estimates]);

  const buildHeaders = () => {
    const headers: any = {};
    if (!isCookieMode && token) {
      headers.Authorization = `Bearer ${token}`;
    }
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    return headers;
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/estimates/${id}`, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      mutate();
    } catch (err) {
      console.error('Failed to delete estimate', err);
    }
  };

  const handleStatusChange = async (id: string, status: 'pending' | 'approved' | 'rejected') => {
    const estimate = transformedEstimates.find((entry) => entry.id === id);
    if (status === 'approved' && !estimate?.converted_invoice_id) {
      setPendingStatusEstimateId(id);
      setPendingStatusValue(status);
      setStatusWarningDialogOpen(true);
      setActionError(null);
      return;
    }

    try {
      setIsUpdatingStatus(true);
      await axios.put(`${API_BASE_URL}/estimates/${id}`, {
        status,
      }, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      mutate();
    } catch (err) {
      console.error('Failed to update estimate status', err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusEstimateId || !pendingStatusValue) return;
    try {
      if (pendingStatusValue === 'approved') {
        setIsConverting(true);
        const response = await axios.post(`${API_BASE_URL}/estimates/${pendingStatusEstimateId}/convert-to-invoice`, {}, {
          headers: buildHeaders(),
          withCredentials: isCookieMode,
        });
        mutate();
        const successMessage = 'Estimate converted successfully.';
        const invoiceId = response.data?.invoiceId;
        setStatusWarningDialogOpen(false);
        setPendingStatusEstimateId(null);
        setPendingStatusValue(null);
        if (invoiceId) {
          navigate(`/invoices/${invoiceId}`, { state: { successMessage } });
          return;
        }
        navigate('/invoices?refresh=true', { state: { successMessage } });
        return;
      }

      setIsUpdatingStatus(true);
      await axios.put(`${API_BASE_URL}/estimates/${pendingStatusEstimateId}`, {
        status: pendingStatusValue,
      }, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      mutate();
      setStatusWarningDialogOpen(false);
      setPendingStatusEstimateId(null);
      setPendingStatusValue(null);
    } catch (err) {
      console.error('Failed to update estimate status', err);
    } finally {
      setIsUpdatingStatus(false);
      setIsConverting(false);
    }
  };

  const handleConvert = async (id: string) => {
    setPendingConvertEstimateId(id);
    setConvertDialogOpen(true);
    setActionError(null);
  };

  const handleConfirmConvert = async () => {
    if (!pendingConvertEstimateId) return;
    try {
      setIsConverting(true);
      const response = await axios.post(`${API_BASE_URL}/estimates/${pendingConvertEstimateId}/convert-to-invoice`, {}, {
        headers: buildHeaders(),
        withCredentials: isCookieMode,
      });
      mutate();
      const successMessage = 'Estimate converted successfully.';
      const invoiceId = response.data?.invoiceId;
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}`, { state: { successMessage } });
      } else {
        navigate('/invoices?refresh=true', { state: { successMessage } });
      }
    } catch (err: any) {
      console.error('Failed to convert estimate', err);
      setActionError(err?.response?.data?.error?.message || 'Failed to convert estimate');
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
        <div className="mb-6 flex flex-col items-start justify-between sm:flex-row sm:items-center">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">Estimates</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage and track your business-facing estimates
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsWizardModalOpen(true)}
            aria-label="Create Estimate"
            className="mt-4 rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra sm:mt-0"
          >
            <FiPlus className="mr-2 h-4 w-4" />
            Create Estimate
          </button>
        </div>

        {fetchError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <p className="text-red-800 dark:text-red-200">
              {fetchError.message || 'Failed to load estimates'}
            </p>
          </div>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        <EstimateTable
          estimates={transformedEstimates}
          loading={isLoading}
          onEdit={(id) => navigate(`/estimates/${id}/edit`)}
          onView={(id) => navigate(`/estimates/${id}`)}
          onDelete={handleDelete}
          onConvert={handleConvert}
          onStatusChange={handleStatusChange}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        {!isReachingEnd && !isEmpty && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              {isLoadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}

        {isEmpty && !isLoading && (
          <div className="py-12 text-center">
            <div className="text-gray-400 dark:text-gray-500">
              <p className="text-lg font-medium">No estimates found</p>
              <p className="mt-2 text-sm">Create your first estimate to get started</p>
            </div>
          </div>
        )}

        <InvoiceFormWizard
          isOpen={isWizardModalOpen}
          onClose={() => setIsWizardModalOpen(false)}
          onSuccess={() => {
            setIsWizardModalOpen(false);
            mutate();
          }}
          mode="estimate"
        />

        <Dialog open={convertDialogOpen} onClose={() => !isConverting && setConvertDialogOpen(false)}>
          <DialogTitle>Convert to Invoice</DialogTitle>
          <DialogContent>
            This will convert this estimate into a new invoice, are you sure?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (isConverting) return;
                setConvertDialogOpen(false);
                setPendingConvertEstimateId(null);
              }}
              disabled={isConverting}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmConvert} variant="contained" disabled={isConverting}>
              {isConverting ? 'Converting...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={statusWarningDialogOpen} onClose={() => !isUpdatingStatus && setStatusWarningDialogOpen(false)}>
          <DialogTitle>Warning</DialogTitle>
          <DialogContent>
            This will convert this estimate into a new invoice and mark it approved. Continue?
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                if (isUpdatingStatus) return;
                setStatusWarningDialogOpen(false);
                setPendingStatusEstimateId(null);
                setPendingStatusValue(null);
              }}
              disabled={isUpdatingStatus}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmStatusChange} variant="contained" disabled={isUpdatingStatus || isConverting}>
              {isUpdatingStatus ? 'Saving...' : isConverting ? 'Converting...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default EstimatesPage;
